import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertTriangle, Plus } from "lucide-react";

const LeaveDialogSection = ({ ctx }) => {
  const {
    calculateDays,
    employees,
    handleLeaveSubmit,
    leaveDialogOpen,
    leaveForm,
    setLeaveDialogOpen,
    setLeaveForm
  } = ctx;
  return (
    <>
            <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
              <DialogTrigger asChild>
                <button data-testid="add-leave-button" className="btn-primary flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Apply Leave</span>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">APPLY FOR LEAVE</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleLeaveSubmit} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Employee *</label>
                    <select
                      required
                      value={leaveForm.employee_id}
                      onChange={(e) => setLeaveForm({...leaveForm, employee_id: e.target.value})}
                      data-testid="leave-employee-select"
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select Employee</option>
                      {employees.filter(e => e.status === "Active").map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.employee_code} - {emp.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Leave Type *</label>
                    <select
                      required
                      value={leaveForm.leave_type}
                      onChange={(e) => setLeaveForm({...leaveForm, leave_type: e.target.value})}
                      data-testid="leave-type-select"
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="Casual">Casual Leave</option>
                      <option value="Sick">Sick Leave</option>
                      <option value="Annual">Annual Leave</option>
                      <option value="Unpaid">Unpaid Leave</option>
                      <option value="Compensatory">Compensatory Off</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Start Date *</label>
                      <input
                        type="date"
                        required
                        value={leaveForm.start_date}
                        onChange={(e) => {
                          const start = e.target.value;
                          const days = calculateDays(start, leaveForm.end_date);
                          setLeaveForm({...leaveForm, start_date: start, days_count: days});
                        }}
                        data-testid="leave-start-date"
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">End Date *</label>
                      <input
                        type="date"
                        required
                        value={leaveForm.end_date}
                        onChange={(e) => {
                          const end = e.target.value;
                          const days = calculateDays(leaveForm.start_date, end);
                          setLeaveForm({...leaveForm, end_date: end, days_count: days});
                        }}
                        data-testid="leave-end-date"
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
                      Days: <span className="font-mono text-primary">{leaveForm.days_count}</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Reason *</label>
                    <textarea
                      required
                      value={leaveForm.reason}
                      onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                      data-testid="leave-reason-input"
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      rows="3"
                      placeholder="Enter reason for leave"
                    />
                  </div>
                  
                  {/* Urgent Leave Warning */}
                  {(() => {
                    const today = new Date();
                    const startDate = new Date(leaveForm.start_date);
                    const daysUntilLeave = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
                    const isUrgent = daysUntilLeave < 7 && daysUntilLeave >= 0;
                    
                    if (isUrgent) {
                      return (
                        <div className="p-4 bg-warning/10 border border-warning/30 rounded-sm">
                          <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-bold text-warning text-sm">URGENT LEAVE REQUEST</p>
                              <p className="text-sm text-slate-700 mt-1">
                                Leave must be applied at least 7 days in advance. This request is within {daysUntilLeave} day(s) 
                                and will require <strong>Management approval</strong>.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={() => setLeaveDialogOpen(false)} className="btn-outline">
                      Cancel
                    </button>
                    <button type="submit" data-testid="submit-leave-button" className="btn-primary">
                      Submit Request
                    </button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
    </>
  );
};

export default LeaveDialogSection;
